/**
 * One chart widget's rows for its current binding (after cross-filter and drill-down), with the data states of
 * screenshots/88: the last good result stays on screen while a new one loads (slow: > 1 s progress line, > 4 s
 * "Still loading · N s" + Cancel), a failed query keeps it (dimmed) with an error id and Retry, and 403 is "not for this
 * viewer". The binding the snapshot already answered is never re-fetched (first paint stays motion-free).
 */
import { useEffect, useRef, useState } from "react";
import { SLOW_LINE_MS, SLOW_TEXT_MS } from "../charts/interactions";
import type { DatasetResult } from "../types";

export interface QueryState {
  ds: DatasetResult | null;           // what's on screen (the last good result)
  loadingMs: number;                  // > 0 while a query is in flight
  error: { id: string; at: string | null } | null;
  forbidden: boolean;
  retry: () => void;
  cancel: () => void;
}

const errorId = () => { const h = Math.random().toString(16).slice(2, 10).padEnd(8, "0"); return `${h.slice(0, 4)}-${h.slice(4, 8)}`; };
const clock = (iso?: string) => (iso ? new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : null);
export { SLOW_LINE_MS, SLOW_TEXT_MS };

export function useDatasetQuery(url: string | null, initial: DatasetResult | undefined): QueryState {
  const [ds, setDs] = useState<DatasetResult | null>(initial ?? null);
  const [loadingMs, setLoadingMs] = useState(0);
  const [error, setError] = useState<QueryState["error"]>(null);
  const [forbidden, setForbidden] = useState(false);
  const [nonce, setNonce] = useState(0);
  const ctl = useRef<AbortController | null>(null);

  // A newer snapshot/loadDataset result for the same widget replaces ours when it answers the same query.
  useEffect(() => { if (initial && initial.query === url) { setDs(initial); setError(null); } }, [initial, url]);

  useEffect(() => {
    if (!url || (initial && initial.query === url && nonce === 0)) return;
    const c = new AbortController();
    ctl.current = c;
    const t0 = performance.now();
    setLoadingMs(1);
    const tick = setInterval(() => setLoadingMs(Math.max(1, performance.now() - t0)), 250);
    fetch(url, { credentials: "include", signal: c.signal })
      .then(async (r) => {
        if (r.status === 403) { setForbidden(true); return; }
        if (!r.ok) throw new Error(String(r.status));
        setForbidden(false);
        setDs((await r.json()) as DatasetResult);
        setError(null);
      })
      .catch((e: Error) => { if (e.name !== "AbortError") setError((prev) => prev ?? { id: errorId(), at: null }); })
      .finally(() => { clearInterval(tick); if (ctl.current === c) { ctl.current = null; setLoadingMs(0); } });
    return () => { clearInterval(tick); c.abort(); };
  }, [url, nonce]); // eslint-disable-line react-hooks/exhaustive-deps

  // "Showing the chart from 6:02 AM": the time of the result that stays on screen.
  const shownError = error ? { ...error, at: clock(ds?.updatedAt) } : null;
  return {
    ds, loadingMs, error: shownError, forbidden,
    retry: () => { setError(null); setNonce((n) => n + 1); },
    cancel: () => { ctl.current?.abort(); ctl.current = null; setLoadingMs(0); },
  };
}
